/**
 * Markdown as READMEs use it: GitHub's block syntax (headings, lists, quotes,
 * fenced code, tables) and inline code spans, links, images, bold, italic and
 * strikethrough. Raw HTML is not rendered: comments are dropped, and other
 * tags are dropped when they are known HTML tags and kept as text otherwise.
 * The output is built as DOM nodes, never parsed from HTML.
 */

export type Inline =
    | { text: string }
    | { code: string }
    | { strong: Inline[] }
    | { em: Inline[] }
    | { del: Inline[] }
    | { br: true }
    | { src: string; alt: string }
    | { href: string; title?: string; children: Inline[] };

export type Block =
    | { kind: "heading"; level: number; slug: string; children: Inline[] }
    | { kind: "paragraph"; children: Inline[] }
    | { kind: "code"; lang: string; text: string }
    | { kind: "quote"; children: Block[] }
    | { kind: "list"; ordered: boolean; start: number; tight: boolean; items: Block[][] }
    | { kind: "table"; align: Align[]; head: Inline[][]; rows: Inline[][][] }
    | { kind: "rule" };

type Align = "left" | "center" | "right" | undefined;

export interface RenderOptions {
    /**
     * Follows a link that is not an absolute URL: a path relative to the
     * document, or a `#fragment`. Without it such a link is only its text.
     */
    link?: (href: string) => void;
    /** A URL for an image whose source is a relative path; undefined shows its alt text. */
    image?: (src: string) => Promise<string | undefined>;
    /**
     * Runs an `idfweb:` action link, with the link's title. Without it such a
     * link is only its text. A paragraph of action links alone shows them as buttons.
     */
    action?: (href: string, title: string | undefined) => void;
}

/** Schemes a link may use, besides `idfweb:` actions; anything else with a scheme, such as `javascript:`, is text. */
const EXTERNAL = /^(https?:|mailto:)/i;
const SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const ACTION = /^idfweb:/i;

const HTML_TAGS = new Set(
    (
        "a abbr b big blockquote br center code dd del details div dl dt em font h1 h2 h3 h4 h5 h6 hr i img " +
        "ins kbd li ol p picture pre s samp small source span strike strong sub summary sup table tbody td " +
        "tfoot th thead tr tt u ul video"
    ).split(" "),
);

const ENTITIES: Record<string, string> = {
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    apos: "'",
    nbsp: " ",
    copy: "©",
    reg: "®",
    trade: "™",
    mdash: "—",
    ndash: "–",
    hellip: "…",
};

export function parseInline(source: string): Inline[] {
    const out: Inline[] = [];
    let text = "";
    const flush = () => {
        if (text !== "") out.push({ text });
        text = "";
    };
    let i = 0;
    while (i < source.length) {
        const rest = source.slice(i);
        // Emphasis starts only at the start of a word, so snake_case names stay as they are.
        const wordStart = !/\w/.test(source[i - 1] ?? "");
        const escape = /^\\([!-/:-@[-`{-~])/.exec(rest);
        const entity = /^&(#\d{1,7}|#x[\da-f]{1,6}|[a-z]+);/i.exec(rest);
        if (escape !== null || (entity !== null && decodeEntity(entity[1]!) !== undefined)) {
            text += escape !== null ? escape[1] : decodeEntity(entity![1]!);
            i += (escape ?? entity)![0].length;
            continue;
        }
        const match =
            /^(`+)([\s\S]*?[^`])\1(?!`)/.exec(rest) ??
            /^(!?)\[((?:[^\]\\]|\\.)*)\]\((?:<([^<>\n]*)>|((?:[^()\s\\]|\\.|\([^()\s]*\))*))(?:\s+"([^"]*)")?\)/.exec(
                rest,
            ) ??
            /^<(https?:\/\/[^>\s]+)>/.exec(rest) ??
            (wordStart ? /^(https?:\/\/[^\s<]*[^\s<.,:;"')\]])/.exec(rest) : null) ??
            /^<!--[\s\S]*?-->/.exec(rest) ??
            /^<\/?([a-z][a-z0-9]*)\b[^<>]*>/i.exec(rest) ??
            /^( {2,}|\\)\n/.exec(rest) ??
            (wordStart ? /^~~(?=\S)([\s\S]+?)(?<=\S)~~/.exec(rest) : null) ??
            (wordStart ? /^(\*\*|__)(?=\S)([\s\S]+?)(?<=\S)\1(?!\w)/.exec(rest) : null) ??
            (wordStart ? /^(\*|_)(?=\S)([\s\S]+?)(?<=\S)\1(?!\w)/.exec(rest) : null);
        const tag =
            match !== null && /^<\/?[a-z]/i.test(match[0]) && !/^<https?:/.test(match[0])
                ? match[1]!.toLowerCase()
                : undefined;
        if (match === null || (tag !== undefined && !HTML_TAGS.has(tag))) {
            text += source[i];
            i++;
            continue;
        }
        flush();
        const [whole, first, second, third, fourth, fifth] = match;
        i += whole.length;
        if (whole.startsWith("`")) out.push({ code: second!.replace(/\n/g, " ").replace(/^ (.*[^ ].*) $/, "$1") });
        else if (whole.startsWith("<!--")) continue;
        else if (tag !== undefined) {
            if (tag === "br") out.push({ br: true });
        } else if (whole.startsWith("<") || whole.startsWith("http")) {
            out.push({ href: first!, children: [{ text: first! }] });
        } else if (whole.startsWith("[") || whole.startsWith("![")) {
            // A destination in <> may hold spaces; a bare one, escaped or balanced parentheses.
            const target = third ?? fourth!;
            const safe = target === "" || EXTERNAL.test(target) || ACTION.test(target) || !SCHEME.test(target);
            if (first === "!") {
                if (safe && target !== "") out.push({ src: target, alt: plainText(parseInline(second!)) });
                else text += second;
            } else if (safe && target !== "") {
                out.push(
                    fifth === undefined
                        ? { href: target, children: parseInline(second!) }
                        : { href: target, title: fifth, children: parseInline(second!) },
                );
            } else out.push(...parseInline(second!));
        } else if (/^( {2,}|\\)\n/.test(whole)) out.push({ br: true });
        else if (whole.startsWith("~~")) out.push({ del: parseInline(first!) });
        else if (first!.length === 2) out.push({ strong: parseInline(second!) });
        else out.push({ em: parseInline(second!) });
    }
    flush();
    return out;
}

function decodeEntity(name: string): string | undefined {
    if (name.startsWith("#")) {
        const code = name[1] === "x" || name[1] === "X" ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
        return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : undefined;
    }
    return ENTITIES[name];
}

function plainText(inlines: Inline[]): string {
    return inlines
        .map((inline) => {
            if ("text" in inline) return inline.text;
            if ("code" in inline) return inline.code;
            if ("alt" in inline) return inline.alt;
            if ("br" in inline) return " ";
            const children =
                "strong" in inline
                    ? inline.strong
                    : "em" in inline
                      ? inline.em
                      : "del" in inline
                        ? inline.del
                        : inline.children;
            return plainText(children);
        })
        .join("");
}

/** A heading's anchor as GitHub makes it: lower case, punctuation dropped, spaces as hyphens. */
export function slugOf(text: string): string {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\p{L}\p{N}\s_-]/gu, "")
        .replace(/\s/g, "-");
}

const FENCE = /^( {0,3})(`{3,}|~{3,})\s*([^`\s]*)[^`]*$/;
const ATX = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const QUOTE = /^ {0,3}> ?(.*)$/;
const ITEM = /^( {0,3})([-*+]|\d{1,9}[.)])([ \t]+|$)(.*)$/;
const SETEXT = /^ {0,3}(=+|-+)[ \t]*$/;
const DELIMITER_ROW = /^ *\|? *:?-+:? *(\| *:?-+:? *)*\|? *$/;
const HTML_BLOCK = /^ {0,3}<(!--|\/?([a-z][a-z0-9]*)(\s|\/?>|$))/i;

export function parseMarkdown(source: string): Block[] {
    const slugs = new Map<string, number>();
    return parseBlocks(source.replace(/\r\n?/g, "\n").replace(/\t/g, "    ").split("\n"), slugs);
}

function parseBlocks(lines: string[], slugs: Map<string, number>): Block[] {
    const blocks: Block[] = [];
    let i = 0;
    const heading = (level: number, text: string) => {
        const children = parseInline(text);
        const base = slugOf(plainText(children));
        const seen = slugs.get(base) ?? 0;
        slugs.set(base, seen + 1);
        blocks.push({ kind: "heading", level, slug: seen === 0 ? base : `${base}-${seen}`, children });
    };
    while (i < lines.length) {
        const line = lines[i]!;
        if (line.trim() === "") {
            i++;
            continue;
        }
        const fence = FENCE.exec(line);
        if (fence !== null) {
            const [, indent, marker, lang] = fence;
            const body: string[] = [];
            i++;
            while (
                i < lines.length &&
                !new RegExp(`^ {0,3}${marker![0] === "`" ? "`" : "~"}{${marker!.length},}\\s*$`).test(lines[i]!)
            ) {
                body.push(lines[i]!.replace(new RegExp(`^ {0,${indent!.length}}`), ""));
                i++;
            }
            i++;
            blocks.push({ kind: "code", lang: lang!, text: body.join("\n") });
            continue;
        }
        const atx = ATX.exec(line);
        if (atx !== null) {
            heading(atx[1]!.length, atx[2] ?? "");
            i++;
            continue;
        }
        if (RULE.test(line)) {
            blocks.push({ kind: "rule" });
            i++;
            continue;
        }
        if (QUOTE.test(line)) {
            const body: string[] = [];
            while (i < lines.length && lines[i]!.trim() !== "") {
                const quoted = QUOTE.exec(lines[i]!);
                body.push(quoted === null ? lines[i]! : quoted[1]!);
                i++;
            }
            blocks.push({ kind: "quote", children: parseBlocks(body, slugs) });
            continue;
        }
        if (ITEM.test(line)) {
            i = parseList(lines, i, blocks, slugs);
            continue;
        }
        if (/^ {4}/.test(line)) {
            const body: string[] = [];
            while (i < lines.length && (/^ {4}/.test(lines[i]!) || lines[i]!.trim() === "")) {
                body.push(lines[i]!.slice(4));
                i++;
            }
            while (body.at(-1)?.trim() === "") body.pop();
            blocks.push({ kind: "code", lang: "", text: body.join("\n") });
            continue;
        }
        const head = line.includes("|") && DELIMITER_ROW.test(lines[i + 1] ?? "") ? cells(line) : [];
        if (head.length > 0 && cells(lines[i + 1]!).length === head.length) {
            const align = cells(lines[i + 1]!).map((cell): Align => {
                const left = cell.startsWith(":");
                const right = cell.endsWith(":");
                return left && right ? "center" : right ? "right" : left ? "left" : undefined;
            });
            i += 2;
            const rows: Inline[][][] = [];
            while (i < lines.length && lines[i]!.trim() !== "" && lines[i]!.includes("|")) {
                rows.push(head.map((_, column) => parseInline(cells(lines[i]!)[column] ?? "")));
                i++;
            }
            blocks.push({ kind: "table", align, head: head.map(parseInline), rows });
            continue;
        }
        const html = HTML_BLOCK.exec(line);
        if (html !== null && (html[1] === "!--" || HTML_TAGS.has(html[2]!.toLowerCase()))) {
            const body: string[] = [];
            const comment = html[1] === "!--";
            while (i < lines.length && (comment ? true : lines[i]!.trim() !== "")) {
                body.push(lines[i]!);
                i++;
                if (comment && body.at(-1)!.includes("-->")) break;
            }
            const children = parseInline(body.join("\n").trim());
            if (plainText(children).trim() !== "") blocks.push({ kind: "paragraph", children });
            continue;
        }
        // A paragraph runs to a blank line or to the start of another block.
        const body = [line.trim()];
        i++;
        let level = 0;
        while (i < lines.length) {
            const next = lines[i]!;
            const setext = SETEXT.exec(next);
            if (setext !== null) {
                level = setext[1]![0] === "=" ? 1 : 2;
                i++;
                break;
            }
            if (next.trim() === "" || FENCE.test(next) || ATX.test(next) || RULE.test(next) || QUOTE.test(next)) break;
            const item = ITEM.exec(next);
            // A list interrupts a paragraph only with a bullet or with 1.
            if (item !== null && item[4] !== "" && (!/^\d/.test(item[2]!) || /^1[.)]$/.test(item[2]!))) break;
            body.push(next.replace(/^ +/, ""));
            i++;
        }
        const text = body.join("\n").replace(/[ \t]+$/, "");
        if (level > 0) heading(level, text);
        else blocks.push({ kind: "paragraph", children: parseInline(text) });
    }
    return blocks;
}

/** Parses the list that starts at `lines[start]`; returns the index after it. */
function parseList(lines: string[], start: number, blocks: Block[], slugs: Map<string, number>): number {
    const first = ITEM.exec(lines[start]!)!;
    const ordered = /^\d/.test(first[2]!);
    const delimiter = first[2]!.slice(-1);
    const items: string[][] = [];
    let tight = true;
    let i = start;
    let indent = 0;
    while (i < lines.length) {
        const line = lines[i]!;
        const item = ITEM.exec(line);
        const sameKind = item !== null && /^\d/.test(item[2]!) === ordered && item[2]!.slice(-1) === delimiter;
        if (sameKind && (items.length === 0 || item[1]!.length < indent)) {
            const spaces = item[3]!.length;
            indent = item[1]!.length + item[2]!.length + (spaces === 0 || spaces > 4 ? 1 : spaces);
            items.push([item[4]!]);
            i++;
            continue;
        }
        if (line.trim() === "") {
            // A blank line ends the list unless the list or an item goes on after it.
            let next = i + 1;
            while (next < lines.length && lines[next]!.trim() === "") next++;
            const after = lines[next];
            if (after === undefined) break;
            const afterItem = ITEM.exec(after);
            const continues =
                /^ */.exec(after)![0].length >= indent ||
                (afterItem !== null &&
                    /^\d/.test(afterItem[2]!) === ordered &&
                    afterItem[2]!.slice(-1) === delimiter &&
                    afterItem[1]!.length < indent);
            if (!continues) break;
            tight = false;
            for (; i < next; i++) items.at(-1)!.push("");
            continue;
        }
        const leading = /^ */.exec(line)![0].length;
        if (leading >= indent) {
            items.at(-1)!.push(line.slice(indent));
            i++;
            continue;
        }
        // A lazy continuation of the item's paragraph.
        if (
            items.at(-1)!.at(-1)!.trim() !== "" &&
            !FENCE.test(line) &&
            !ATX.test(line) &&
            !RULE.test(line) &&
            item === null &&
            !QUOTE.test(line)
        ) {
            items.at(-1)!.push(line.trim());
            i++;
            continue;
        }
        break;
    }
    const parsed = items.map((item) => {
        // A blank line between an item's blocks makes the list loose; a trailing one does not.
        while (item.at(-1)?.trim() === "") item.pop();
        return parseBlocks(item, slugs);
    });
    blocks.push({ kind: "list", ordered, start: ordered ? parseInt(first[2]!, 10) : 1, tight, items: parsed });
    return i;
}

function cells(row: string): string[] {
    const trimmed = row
        .trim()
        .replace(/^\|/, "")
        .replace(/(?<!\\)\|$/, "");
    return trimmed.split(/(?<!\\)\|/).map((cell) => cell.trim().replace(/\\\|/g, "|"));
}

export function renderInline(source: string, options: RenderOptions = {}): DocumentFragment {
    const fragment = document.createDocumentFragment();
    fragment.append(...parseInline(source).map((inline) => toNode(inline, options)));
    return fragment;
}

export function renderMarkdown(source: string, options: RenderOptions = {}): DocumentFragment {
    const fragment = document.createDocumentFragment();
    fragment.append(...parseMarkdown(source).map((block) => blockNode(block, options, false)));
    return fragment;
}

function blockNode(block: Block, options: RenderOptions, tight: boolean): Node {
    switch (block.kind) {
        case "heading": {
            const node = inlineElement(`h${block.level}`, block.children, options);
            node.id = block.slug;
            return node;
        }
        case "paragraph":
            if (options.action !== undefined && isActionRow(block.children)) {
                const row = document.createElement(tight ? "div" : "p");
                row.className = "md-actions";
                for (const inline of block.children) {
                    if ("href" in inline) row.append(actionButton(inline, options.action));
                }
                return row;
            }
            if (tight) {
                const fragment = document.createDocumentFragment();
                fragment.append(...block.children.map((inline) => toNode(inline, options)));
                return fragment;
            }
            return inlineElement("p", block.children, options);
        case "code": {
            const pre = document.createElement("pre");
            const code = document.createElement("code");
            if (block.lang !== "") code.dataset.lang = block.lang;
            code.textContent = block.text;
            pre.append(code);
            return pre;
        }
        case "quote": {
            const quote = document.createElement("blockquote");
            quote.append(...block.children.map((child) => blockNode(child, options, false)));
            return quote;
        }
        case "list": {
            const list = document.createElement(block.ordered ? "ol" : "ul");
            if (block.ordered && block.start !== 1) (list as HTMLOListElement).start = block.start;
            for (const item of block.items) {
                const li = document.createElement("li");
                li.append(...item.map((child) => blockNode(child, options, block.tight)));
                list.append(li);
            }
            return list;
        }
        case "table": {
            const table = document.createElement("table");
            const row = (cells: Inline[][], tag: "th" | "td") => {
                const tr = document.createElement("tr");
                cells.forEach((cell, column) => {
                    const node = inlineElement(tag, cell, options);
                    const align = block.align[column];
                    if (align !== undefined) node.style.textAlign = align;
                    tr.append(node);
                });
                return tr;
            };
            const head = document.createElement("thead");
            head.append(row(block.head, "th"));
            const body = document.createElement("tbody");
            body.append(...block.rows.map((cells) => row(cells, "td")));
            table.append(head, body);
            return table;
        }
        case "rule":
            return document.createElement("hr");
    }
}

function toNode(inline: Inline, options: RenderOptions): Node {
    if ("text" in inline) return document.createTextNode(inline.text);
    if ("code" in inline) return inlineElement("code", [{ text: inline.code }], options);
    if ("strong" in inline) return inlineElement("strong", inline.strong, options);
    if ("em" in inline) return inlineElement("em", inline.em, options);
    if ("del" in inline) return inlineElement("del", inline.del, options);
    if ("br" in inline) return document.createElement("br");
    if ("src" in inline) return image(inline.src, inline.alt, options);
    const children = inline.children.map((child) => toNode(child, options));
    const link = document.createElement("a");
    link.append(...children);
    link.href = inline.href;
    link.title = inline.title ?? inline.href;
    if (ACTION.test(inline.href)) {
        const run = options.action;
        if (run === undefined) {
            const fragment = document.createDocumentFragment();
            fragment.append(...children);
            return fragment;
        }
        link.addEventListener("click", (event) => {
            event.preventDefault();
            run(inline.href, inline.title);
        });
        return link;
    }
    if (EXTERNAL.test(inline.href)) {
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        return link;
    }
    const follow = options.link;
    if (follow === undefined) {
        const fragment = document.createDocumentFragment();
        fragment.append(...children);
        return fragment;
    }
    link.addEventListener("click", (event) => {
        event.preventDefault();
        follow(inline.href);
    });
    return link;
}

/** Whether a paragraph holds only `idfweb:` links, with nothing but spaces between them. */
function isActionRow(inlines: Inline[]): boolean {
    let links = 0;
    for (const inline of inlines) {
        if ("href" in inline && ACTION.test(inline.href)) links++;
        else if (!("text" in inline) || inline.text.trim() !== "") return false;
    }
    return links > 0;
}

function actionButton(
    inline: Extract<Inline, { href: string }>,
    run: NonNullable<RenderOptions["action"]>,
): HTMLElement {
    const button = document.createElement("button");
    button.type = "button";
    button.append(...inline.children.map((child) => toNode(child, {})));
    if (inline.title !== undefined) button.title = inline.title;
    button.addEventListener("click", () => run(inline.href, inline.title));
    return button;
}

function image(src: string, alt: string, options: RenderOptions): Node {
    const img = document.createElement("img");
    img.alt = alt;
    if (EXTERNAL.test(src)) {
        // The page is cross-origin isolated: an image from another site loads only with CORS.
        img.crossOrigin = "anonymous";
        img.src = src;
        return img;
    }
    if (options.image === undefined) return document.createTextNode(alt);
    void options.image(src).then((url) => {
        if (url !== undefined) img.src = url;
        else img.replaceWith(alt);
    });
    return img;
}

function inlineElement(tag: string, children: Inline[], options: RenderOptions): HTMLElement {
    const node = document.createElement(tag);
    node.append(...children.map((child) => toNode(child, options)));
    return node;
}
